import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaults } from "../_helpers/options-with-defaults";
import { Options, UseQueryOptionsNoKeyAndFn } from "../_types";

export const APP_ROLE_GROUP_SEARCH_RESOURCE = "app-role" as const;
export const APP_ROLE_GROUP_SEARCH_OPERATION = "group-search" as const;
export const APP_ROLE_GROUP_SEARCH_API_PATH =
  `/v1/${APP_ROLE_GROUP_SEARCH_RESOURCE}/${APP_ROLE_GROUP_SEARCH_OPERATION}` as const;

export type AppRoleGroupSearchParameters = paths[typeof APP_ROLE_GROUP_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type AppRoleGroupSearchRequestBody =
  paths[typeof APP_ROLE_GROUP_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type AppRoleGroupSearchResponse =
  paths[typeof APP_ROLE_GROUP_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useAppRoleGroupSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: Options<AppRoleGroupSearchParameters, AppRoleGroupSearchRequestBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    AppRoleGroupSearchResponse,
    Error,
    AppRoleGroupSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaultsResolved =
    optionsWithDefaults<Options<AppRoleGroupSearchParameters, AppRoleGroupSearchRequestBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaultsResolved.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaultsResolved.parameters),
  };

  return useQuery({
    queryKey: [
      APP_ROLE_GROUP_SEARCH_RESOURCE,
      APP_ROLE_GROUP_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaultsResolved.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<AppRoleGroupSearchResponse>(
        APP_ROLE_GROUP_SEARCH_API_PATH,
        optionsWithDefaultsResolved.body,
        { params: requestParameters },
      );

      return response.data;
    },
    ...queryOptions,
    enabled: (enabled ?? true) && !!optionsWithDefaultsResolved.body?.appRole,
  });
}
